import { React, useContext, useEffect, useState, useId } from "react";
import { AppContext } from "../App";
import { callApi, getInlineLoader, setCookie } from "../Helpers";
import { FiBriefcase, FiArrowRight, FiLock, FiList, FiUser, FiMail, FiAlertCircle } from "react-icons/fi";
import { MdSearch } from "react-icons/md";
import OneApplication from "../ones/OneApplication"; 

export default function MyApplications() {
  const appContext = useContext(AppContext);
  const scopeId = useId().replace(/:/g, "");

  /* States */
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);

  /* Status Categories */
  const [submittedRecs, setSubmittedRecs] = useState([]);
  const [acceptedRecs, setAcceptedRecs] = useState([]);
  const [rejectedRecs, setRejectedRecs] = useState([]);
  const [hiredRecs, setHiredRecs] = useState([]);
  
  const [allApplications, setAllApplications] = useState([]);
  const [recsList, setRecsList] = useState([]);

  const [filter, setFilter] = useState('');
  const [category, setCategory] = useState('all');

  /* Auth Flow States */
  const [isApplying, setIsApplying] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [showOtpPrompt, setShowOtpPrompt] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [otpInput, setOtpInput] = useState("");

  async function init() {
    setReady(false);
    await Promise.all([
      (async () => {
        const isAuthenticated = await appContext?.authCheck?.();
        if (isAuthenticated) {
          const isProfileValid = await verifyProfileCompleteness();
          if (isProfileValid) {
            await fetchApplications();
          }
        } else {
          setShowEmailPrompt(true);
          setShowOtpPrompt(false);
        }
      })(),
    ]);
    setReady(true);
  }

  async function verifyProfileCompleteness() {
    try {
      const response = await callApi('jobapplicant/get-current-applicant-data', {});
      if (response && response.status === 1) {
        const applicant = response.data;
        
        const isIncomplete = 
          !applicant ||
          !applicant.firstName?.trim() ||
          !applicant.middleName?.trim() ||
          !applicant.lastName?.trim() ||
          !applicant.phone?.trim() ||
          !applicant.physicalAddress?.trim() ||
          !applicant.dob?.trim() ||
          !applicant.gender?.trim();

        if (isIncomplete) {
          appContext?.tellError?.("Please fill out your core profile records details completely.");
          appContext?.navTo?.({
            item: 'side-view',
            subItem: 'edit-applicant-profile'
          });
          return false;
        }
        return true;
      } else {
        appContext?.tellError?.(response.msg || "Could not retrieve verification profile files.");
        return false;
      }
    } catch (err) {
      appContext?.tellError?.("Network connection error verification loop.");
      return false;
    }
  }

  async function fetchApplications() {
    await callApi('jobapplicant/get-my-job-applications', {}).then((response) => {
      if (response && response.status === 1) {
        setAllApplications(response.data || []);
        setShowEmailPrompt(false);
        setShowOtpPrompt(false);
      } else {
        appContext?.tellError?.(response.msg || "Could not fetch applications data");
      }
    }).catch(() => {
      appContext?.tellError?.("Network error occurred fetching history.");
    });
  }

  function openProfileEdit() {
    appContext?.navTo?.({
      item: 'side-view',
      subItem: 'edit-applicant-profile'
    });
  }

  function categorizeRecs() {
    if (allApplications) {
      const _submitted = allApplications.filter(item => {
        const status = String(item.status).toLowerCase();
        return (status === 'submitted' || status === 'under-review' || status === 'review' || status === 'pending');
      });

      const _accepted = allApplications.filter(item => {
        const status = String(item.status).toLowerCase();
        return (status === 'accepted' || status === 'approved');
      });

      const _rejected = allApplications.filter(item => {
        const status = String(item.status).toLowerCase();
        return (status === 'rejected' || status === 'declined' || status === 'unsuccessful');
      });

      const _hired = allApplications.filter(item => {
        const status = String(item.status).toLowerCase();
        return (status === 'hired' || status === 'selected' || status === 'employed');
      });

      setSubmittedRecs(_submitted);
      setAcceptedRecs(_accepted);
      setRejectedRecs(_rejected);
      setHiredRecs(_hired);
    }
  }

  function processRecs() {
    let _list = allApplications;

    if (category === 'submitted') {
      _list = submittedRecs;
    } else if (category === 'accepted') {
      _list = acceptedRecs;
    } else if (category === 'rejected') {
      _list = rejectedRecs;
    } else if (category === 'hired') {
      _list = hiredRecs;
    }

    if (filter && filter.trim().length > 0) {
      const _filtered = _list?.filter(item => {
        return String(item.jobTitle).toLowerCase().indexOf(String(filter).toLowerCase()) > -1;
      });
      setRecsList(_filtered);
    } else {
      setRecsList(_list);
    }
  }

  async function handleEmailSubmit() {
    if (!emailInput || !emailInput.includes('@')) {
      appContext?.tellError?.("Please enter a valid email address.");
      return;
    }
    setIsApplying(true);
    await callApi('jobapplicant/access-account', { email: emailInput }).then(response => {
      if (response.status === 1) {
        appContext?.tellMessage?.(response.msg);
        setShowOtpPrompt(true); 
      } else {
        appContext?.tellError?.(response.msg || "Failed to process request.");
      }
    }).catch(() => appContext?.tellError?.("Network error occurred."));
    setIsApplying(false);
  }

  async function handleOtpSubmit() {
    if (!otpInput) {
      appContext?.tellError?.("Please enter the OTP.");
      return;
    }
    setIsApplying(true);
    await callApi('jobapplicant/verify-otp', { email: emailInput, otp: otpInput }).then(async response => {
      if (response.status === 1) {
        appContext?.tellMessage?.(response.msg);
        setCookie('userUuid', response.data.uuid, 30);
        setCookie('userToken', response.data.token, 30);
        await appContext?.authCheck?.();
        resetPrompts();
        
        const isProfileValid = await verifyProfileCompleteness();
        if (isProfileValid) {
          await fetchApplications();
          setReady(true);
        }
      } else {
        appContext?.tellError?.(response.msg || "Invalid OTP.");
      }
    }).catch(() => appContext?.tellError?.("Network error occurred."));
    setIsApplying(false);
  }

  function resetPrompts() {
    setShowEmailPrompt(false);
    setShowOtpPrompt(false);
    setEmailInput("");
    setOtpInput("");
  }

  useEffect(() => { init(); }, []);
  useEffect(() => { categorizeRecs(); }, [allApplications]);
  useEffect(() => { processRecs(); }, [filter, category, allApplications, submittedRecs, acceptedRecs, rejectedRecs, hiredRecs]);

  useEffect(() => {
    appContext?.setShowOverlayLoader?.(loading || isApplying);
    return () => {
      appContext?.setShowOverlayLoader?.(false);
    };
  }, [loading, isApplying]);

  if (ready) {
    return (
      <>
        <style>{`
          .${scopeId}-view {
            background-color: #091118;
            color: #f3f4f6;
            min-height: 100%;
            font-family: system-ui, -apple-system, sans-serif;
            padding: 24px;
          }

          /* --- Header Area --- */
          .${scopeId}-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid rgba(255, 255, 255, 0.08);
            padding-bottom: 20px;
            margin-bottom: 24px;
            gap: 12px;
          }

          .${scopeId}-title-group {
            display: flex;
            align-items: center;
            gap: 10px;
          }

          .${scopeId}-title {
            font-size: 1.35rem;
            font-weight: 800;
            color: #ffffff;
            margin: 0;
            letter-spacing: -0.02em;
          }

          .${scopeId}-profile-btn {
            background: transparent;
            border: 1px solid rgba(255, 255, 255, 0.15);
            color: #ffffff;
            padding: 8px 14px;
            font-size: 0.75rem;
            font-weight: 700;
            border-radius: 4px;
            display: flex;
            align-items: center;
            gap: 6px;
            cursor: pointer;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            transition: all 0.2s ease;
          }

          .${scopeId}-profile-btn:hover {
            background: rgba(255, 255, 255, 0.05);
            border-color: #ffffff;
          }

          /* --- Metrics Grid --- */
          .${scopeId}-metrics-grid {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 12px;
            margin-bottom: 24px;
          }

          .${scopeId}-metric-card {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.06);
            border-radius: 4px;
            padding: 12px;
            text-align: center;
          }

          .${scopeId}-metric-num {
            font-size: 1.4rem;
            font-weight: 800;
            color: #ffffff;
            line-height: 1.1;
            margin-bottom: 4px;
          }

          .${scopeId}-metric-label {
            font-size: 0.65rem;
            font-weight: 700;
            text-transform: uppercase;
            letter-spacing: 0.5px;
            color: rgba(255, 255, 255, 0.4);
          }

          /* --- Filters Block --- */
          .${scopeId}-controls {
            display: flex;
            gap: 10px;
            margin-bottom: 20px;
          }

          .${scopeId}-search-wrapper {
            position: relative;
            flex: 1;
          }

          .${scopeId}-search-icon {
            position: absolute;
            left: 10px;
            top: 50%;
            transform: translateY(-50%);
            color: rgba(255, 255, 255, 0.35);
          }

          .${scopeId}-search-input {
            width: 100%;
            background: #050b11;
            border: 1px solid rgba(255, 255, 255, 0.12);
            border-radius: 4px;
            padding: 9px 10px 9px 34px;
            color: #ffffff;
            font-size: 0.85rem;
            outline: none;
          }

          .${scopeId}-select {
            background: #050b11;
            border: 1px solid rgba(255, 255, 255, 0.12);
            border-radius: 4px;
            padding: 0 24px 0 10px;
            color: #ffffff;
            font-size: 0.85rem;
            outline: none;
            appearance: none;
            background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='rgba(255,255,255,0.4)' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'></polyline></svg>");
            background-repeat: no-repeat;
            background-position: right 8px center;
            background-size: 12px;
          }

          /* --- Auth Gate Overlays --- */
          .${scopeId}-gate-box {
            background: rgba(255, 255, 255, 0.02);
            border: 1px solid rgba(255, 255, 255, 0.06);
            border-radius: 4px;
            padding: 24px;
            text-align: center;
          }

          .${scopeId}-gate-icon {
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.04);
            color: var(--secondaryColor, #fff);
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 16px auto;
            border: 1px solid rgba(255, 255, 255, 0.08);
          }

          .${scopeId}-gate-text {
            font-size: 0.85rem;
            color: rgba(255, 255, 255, 0.5);
            margin-bottom: 16px;
            line-height: 1.5;
          }

          .${scopeId}-gate-input {
            width: 100%;
            background: #050b11;
            border: 1px solid rgba(255, 255, 255, 0.12);
            border-radius: 4px;
            padding: 12px;
            color: #ffffff;
            font-size: 0.9rem;
            outline: none;
            text-align: center;
            margin-bottom: 16px;
          }

          .${scopeId}-gate-actions {
            display: grid;
            grid-template-columns: 2fr 1fr;
            gap: 8px;
          }

          .${scopeId}-btn-primary {
            padding: 12px;
            font-size: 0.8rem;
            font-weight: 800;
            text-transform: uppercase;
            letter-spacing: 1px;
            border-radius: 4px;
            border: none;
            cursor: pointer;
            background: var(--text-primary, #ffffff);
            color: #050b11;
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 6px;
          }

          .${scopeId}-btn-cancel {
            background: transparent;
            border: 1px solid rgba(255, 255, 255, 0.15);
            color: #ffffff;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 700;
            cursor: pointer;
            text-transform: uppercase;
          }

          /* --- Empty States --- */
          .${scopeId}-empty {
            text-align: center;
            padding: 48px 20px;
            border: 1px dashed rgba(255, 255, 255, 0.1);
            border-radius: 4px;
            background: rgba(255, 255, 255, 0.01);
          }

          /* --- List Layout Stack --- */
          .${scopeId}-list-stack {
            display: flex;
            flex-direction: column;
            gap: 12px;
          }

          @media (max-width: 480px) {
            .${scopeId}-metrics-grid { grid-template-columns: repeat(2, 1fr); }
            .${scopeId}-controls { flex-direction: column; }
            .${scopeId}-select { padding: 10px; background-position: right 12px center; }
          }
        `}</style>

        <div className={`${scopeId}-view`}>
          {/* Header Layout */}
          <header className={`${scopeId}-header`}>
            <div className={`${scopeId}-title-group`}>
              <FiBriefcase size={16} style={{ color: 'var(--secondaryColor)' }} />
              <h1 className={`${scopeId}-title`}>My Applications</h1>
            </div>

            {!showEmailPrompt && !showOtpPrompt && (
              <button className={`${scopeId}-profile-btn`} onClick={openProfileEdit}>
                <FiUser size={13} />
                <span>Profile Settings</span>
              </button>
            )}
          </header>

          {showEmailPrompt || showOtpPrompt ? (
            showOtpPrompt ? (
              /* Verification Gate */
              <div className={`${scopeId}-gate-box`}>
                <div className={`${scopeId}-gate-icon`}><FiLock size={20} /></div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>Security Token Verification</div>
                <p className={`${scopeId}-gate-text`}>A single-use verification payload was routed to {emailInput}.</p>
                <input 
                  type="text" 
                  className={`${scopeId}-gate-input`} 
                  style={{ letterSpacing: '4px', fontWeight: 'bold' }} 
                  placeholder="000000" 
                  value={otpInput} 
                  onChange={(e) => setOtpInput(e.target.value)} 
                  autoFocus 
                />
                <div className={`${scopeId}-gate-actions`}>
                  <button className={`${scopeId}-btn-primary`} onClick={handleOtpSubmit} disabled={isApplying}>
                    <span>Verify Token</span>
                    <FiArrowRight size={14} />
                  </button>
                  <button className={`${scopeId}-btn-cancel`} onClick={() => appContext?.navBack?.()}>Cancel</button>
                </div>
              </div>
            ) : (
              /* Identity Gate */
              <div className={`${scopeId}-gate-box`}>
                <div className={`${scopeId}-gate-icon`}><FiLock size={20} /></div>
                <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#ffffff', marginBottom: '6px' }}>Authentication Required</div>
                <p className={`${scopeId}-gate-text`}>Provide your registered pipeline email to cross-reference and track application records.</p>
                <input 
                  type="email" 
                  className={`${scopeId}-gate-input`} 
                  placeholder="operator@company.com" 
                  value={emailInput} 
                  onChange={(e) => setEmailInput(e.target.value)} 
                  autoFocus 
                />
                <div className={`${scopeId}-gate-actions`}>
                  <button className={`${scopeId}-btn-primary`} onClick={handleEmailSubmit} disabled={isApplying}>
                    <span>Continue</span>
                    <FiArrowRight size={14} />
                  </button>
                  <button className={`${scopeId}-btn-cancel`} onClick={() => appContext?.navBack?.()}>Cancel</button>
                </div>
              </div>
            )
          ) : (
            /* Main Dashboard Logs View */
            <>
              {/* Metrics Metrics Dashboard Context Panel */}
              <div className={`${scopeId}-metrics-grid`}>
                <div className={`${scopeId}-metric-card`}>
                  <div className={`${scopeId}-metric-num`}>{submittedRecs?.length || 0}</div>
                  <div className={`${scopeId}-metric-label`}>Submitted</div>
                </div>
                <div className={`${scopeId}-metric-card`}>
                  <div className={`${scopeId}-metric-num`}>{acceptedRecs?.length || 0}</div>
                  <div className={`${scopeId}-metric-label`}>Accepted</div>
                </div>
                <div className={`${scopeId}-metric-card`}>
                  <div className={`${scopeId}-metric-num`}>{rejectedRecs?.length || 0}</div>
                  <div className={`${scopeId}-metric-label`}>Rejected</div>
                </div>
                <div className={`${scopeId}-metric-card`}>
                  <div className={`${scopeId}-metric-num`}>{hiredRecs?.length || 0}</div>
                  <div className={`${scopeId}-metric-label`}>Selected</div>
                </div>
              </div>

              {/* Filtering Command Node Controls */}
              <div className={`${scopeId}-controls`}>
                <div className={`${scopeId}-search-wrapper`}>
                  <MdSearch className={`${scopeId}-search-icon`} size={18} />
                  <input 
                    className={`${scopeId}-search-input`} 
                    onChange={(e) => setFilter(e.target.value)} 
                    placeholder="Filter historical runs..." 
                  />
                </div>
                
                <select className={`${scopeId}-select`} value={category} onChange={(e) => setCategory(e.target.value)}>
                  <option value="all">All Entries</option>
                  <option value="submitted">Submitted</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="hired">Selected</option>
                </select>
              </div>

              {/* Dynamic Stack Output Layout */}
              <div>
                {filter && filter.trim().length > 0 && (
                  <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginBottom: '10px' }}>
                    Filtering by trace keyword: <span style={{ color: '#fff', fontWeight: 600 }}>{filter}</span>
                  </div>
                )}

                <div className={`${scopeId}-list-stack`}>
                  {recsList && recsList.length > 0 ? (
                    recsList.map((item, i) => (
                      <div key={item.uuid || i}>
                        <OneApplication data={item} />
                      </div>
                    ))
                  ) : (
                    <div className={`${scopeId}-empty`}>
                      <FiList size={20} style={{ color: 'var(--secondaryColor)', marginBottom: '8px', opacity: 0.6 }} />
                      <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', margin: 0 }}>No runtime logs matching this metric scope.</p>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </>
    );
  } else {
    return (
      <div style={{ minHeight: '30vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {getInlineLoader()}
      </div>
    );
  }
}